import gsap from 'gsap';

import SplitText     from 'gsap/SplitText.js';
import SnowAnimation from './SnowAnimation.js';
import ScrollyVideo from './ScrollyVideo.js';
import gsapManager  from './GSAPManager.js';

gsap.registerPlugin(SplitText);

const HERO_TITLE_ANIMATION_IOS = {
    opacity  : 0,
    y        : 24,
    duration : 0.5,
    ease     : 'power2.out'
};

const HERO_TITLE_ANIMATION_DESKTOP = {
    opacity  : 0,
    y        : 10,
    filter   : 'blur(3px)',
    stagger  : 0.1,
    duration : 0.5,
    ease     : 'power2.out'
};

const HERO_BUTTON_ANIMATION_IOS = {
    opacity  : 0,
    y        : 12,
    scale    : 0.95,
    duration : 0.35,
    ease     : 'power2.out'
};

const HERO_BUTTON_ANIMATION_DESKTOP = {
    opacity  : 0,
    y        : 10,
    filter   : 'blur(3px)',
    stagger  : 0.1,
    duration : 0.5,
    ease     : 'power2.out'
};

/**
 * Controls main hero video experience with ScrollyVideo.
 */
class VideoExperience {
    /**
     * @param {Object} options
     * @param {HTMLElement} options.shellElement
     * @param {HTMLElement} options.overlayElement
     */
    constructor({
        shellElement,
        overlayElement
    }) {
        this.shellElement   = shellElement;
        this.overlayElement = overlayElement;

        this.videoSrc                           = '';
        this.scrollyInstance                    = null;
        this.timeline                           = null;
        this.isReady                            = false;
        this.shouldRunStageOne                  = false;
        this.hasStageOneCompleted               = false;
        this.stageOneTargetPercentage           = 0.1875;
        this.stageTwoTargetPercentage           = 0.375;
        this.stageThreeTargetPercentage         = 0.875;
        this.heroIntroDelay                     = 0;
        this.heroIntroShown                     = false;
        this.heroIntroPending                   = false;
        this.snowAnimation                      = null;
        this.hasStageTwoShown                   = false;
        this.hasStageThreeShown                 = false;
        this.isStageOneInteractive              = false;
        this.isStageTwoInteractive              = false;
        this.isStageThreeInteractive            = false;
        this.isReversingToStageOne              = false;
        this.hasStageOneRevealPending           = false;
        this.isReversingToStageTwo              = false;
        this.hasStageTwoRevealPending           = false;
        this.isReversingFromScrollMode          = false;
        this.hasStageTwoRevealPendingFromScroll = false;
        this.reverseScrollModeTransitionSpeed   = 1.1;
        this.forwardStageTransitionSpeed        = 1;
        this.reverseStageTransitionSpeed        = 1.1;
        this.isIOS                              = VideoExperience.detectIOS();
        this.isSafari                           = VideoExperience.detectSafari();
        this.isMobile                           = VideoExperience.detectMobile();

        this.handleVideoTimeUpdate = this.handleVideoTimeUpdate.bind(this);

        if (this.isSafari && typeof document !== 'undefined') {
            const experienceRootElement = document.querySelector('.experience');

            if (experienceRootElement) {
                experienceRootElement.classList.add('experience--is-safari');
            }
        }
    }

    static getVideoToScrollDurationSeconds() {
        return 3;
    }

    static isDebugEnabled() {
        try {
            return window.localStorage && window.localStorage.getItem('experienceDebug') === '1';
        } catch (error) {
            return false;
        }
    }

    static detectIOS() {
        if (typeof navigator === 'undefined') {
            return false;
        }

        const userAgent   = navigator.userAgent || navigator.vendor || '';
        const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);

        const isTouchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

        return isIOSDevice || isTouchMac;
    }

    static detectMobile() {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return false;
        }

        return window.matchMedia('(max-width: 1080px)').matches;
    }

    static detectSafari() {
        if (typeof navigator === 'undefined') {
            return false;
        }

        const userAgent = navigator.userAgent || '';
        const isSafari = /Safari\//.test(userAgent)
            && !/Chrome\//.test(userAgent)
            && !/Chromium\//.test(userAgent)
            && !/CriOS\//.test(userAgent)
            && !/Edg\//.test(userAgent)
            && !/FxiOS\//.test(userAgent);

        return isSafari;
    }

    getHeroBackgroundAnimationConfig() {
        if (this.isSafari) {
            return {
                opacity  : 0,
                duration : 0.6,
                ease     : 'power2.out'
            };
        }

        return {
            opacity  : 0,
            scale    : 1.1,
            duration : 0.6,
            ease     : 'power2.out'
        };
    }

    /**
     * Prepare ScrollyVideo instance once video blob url is ready.
     * @param {string} videoSrc
     */
    prepare(videoSrc) {
        if (!this.shellElement || !videoSrc) {
            return;
        }

        this.videoSrc = videoSrc;

        this.scrollyInstance = new ScrollyVideo({
            scrollyVideoContainer : this.shellElement,
            src                   : this.videoSrc,
            cover                 : true,
            full                  : false,
            sticky                : false,
            trackScroll           : false,
            lockScroll            : false,
            useWebCodecs          : false,
            debug                 : VideoExperience.isDebugEnabled(),
            onChange              : () => {
                // eslint-disable-next-line no-console
                // console.log('[ScrollyVideo] onChange percentage:', percentage);
            },
            onReady : () => {
                this.isReady = true;
                // eslint-disable-next-line no-console
                console.log('[VideoExperience] onReady fired, instance is ready');

                const hasCanvas = !!this.scrollyInstance.canvas;
                // eslint-disable-next-line no-console
                console.log('[VideoExperience] WebCodecs canvas mode', {
                    hasCanvas
                });

                if (typeof this.scrollyInstance.setVideoPercentage === 'function') {
                    // eslint-disable-next-line no-console
                    console.log('[VideoExperience] setVideoPercentage(0)');
                    this.scrollyInstance.setVideoPercentage(0, { transitionSpeed : 0 });
                }

                if (this.shouldRunStageOne) {
                    this.videoStageOne();
                }
            }
        });

        if (this.scrollyInstance && this.scrollyInstance.isSafari) {
            // eslint-disable-next-line no-console
            // console.log('[VideoExperience] Detected Safari engine, disabling ScrollyVideo Safari branch');
            this.scrollyInstance.isSafari = false;
        }

        if (this.scrollyInstance && this.scrollyInstance.video) {
            this.scrollyInstance.video.addEventListener('timeupdate', this.handleVideoTimeUpdate);
        }
    }

    /**
     * Start intro animation after preloader.
     */
    startIntro() {
        if (!this.shellElement) {
            return;
        }

        this.shellElement.style.setProperty('--tilt-x', '0');
        this.shellElement.style.setProperty('--tilt-y', '0');

        gsap.killTweensOf(this.shellElement);

        if (this.timeline) {
            this.timeline.kill();
            this.timeline = null;
        }

        this.videoStageOne();
    }

    /**
     * Handle ScrollyVideo percentage changes (debug / future use).
     * @param {number} percentage
     */
    handleVideoChange(percentage) {
        if (!this.hasStageOneCompleted) {
            return;
        }

        if (this.isReversingToStageOne && this.hasStageOneRevealPending) {
            const revealThreshold = this.stageOneTargetPercentage + 0.02;

            if (percentage <= revealThreshold) {
                this.showHeroStageOneSimple();
                this.hasStageOneRevealPending = false;
                this.isReversingToStageOne    = false;
            }
        }

        if (this.isReversingToStageTwo && this.hasStageTwoRevealPending) {
            const stageTwoThreshold = 0.01;

            if (Math.abs(percentage - this.stageTwoTargetPercentage) <= stageTwoThreshold) {
                this.showHeroStageTwoSimple();
                this.hasStageTwoRevealPending = false;
                this.isReversingToStageTwo    = false;
            }
        }

        if (this.isReversingFromScrollMode && this.hasStageTwoRevealPendingFromScroll) {
            const stageTwoThreshold = 0.01;

            if (Math.abs(percentage - this.stageTwoTargetPercentage) <= stageTwoThreshold) {
                this.showHeroStageTwoSimple();
                this.hasStageTwoRevealPendingFromScroll = false;
                this.isReversingFromScrollMode          = false;
            }
        }
    }

    /**
     * Handle native video timeupdate to trigger stage-two hero
     * exactly when video stops near target percentage.
     */
    handleVideoTimeUpdate() {
        if (!this.scrollyInstance || !this.scrollyInstance.video) {
            return;
        }

        const { video } = this.scrollyInstance;

        if (!video.duration || !Number.isFinite(video.duration)) {
            return;
        }

        const percentage = video.currentTime / video.duration;
        const threshold  = 0.01;

        this.scrollyInstance.currentTime     = video.currentTime;
        this.scrollyInstance.videoPercentage = percentage;

        // eslint-disable-next-line no-console
        // console.log('[VideoExperience] timeupdate', {
        //     percentage,
        //     currentTime : video.currentTime,
        //     timestamp   : (typeof performance !== 'undefined' ? performance.now() : Date.now())
        // });

        this.handleVideoChange(percentage);

        if (this.heroIntroPending && percentage >= this.stageOneTargetPercentage - threshold) {
            this.heroIntroPending = false;
            this.showHeroIntro();
        }

        if (!this.hasStageOneCompleted) {
            return;
        }

        if (!this.hasStageTwoShown && percentage >= this.stageTwoTargetPercentage - threshold) {
            this.showHeroStageTwo();
        }
    }

    /**
     * Animate hero overlay content appearance near end of stage-one.
     */
    showHeroIntro() {
        if (!this.overlayElement || this.heroIntroShown) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-1')
            || this.overlayElement.querySelector('.experience-hero__content');

        if (!contentElement) {
            return;
        }
        const headerElement     = contentElement.querySelector('.experience-hero__header');
        const titleElement      = contentElement.querySelector('.experience-hero__title');
        const buttonElement     = contentElement.querySelector('.experience-hero__button');
        const backgroundElement = contentElement.querySelector('.experience-hero__background');

        this.heroIntroShown = true;

        // Ensure content container is visible before animating its children
        gsap.set(contentElement, {
            opacity : 1,
            y       : 0
        });

        // Start snow when hero content is introduced
        this.startSnow();

        const timeline = gsap.timeline({
            delay      : this.heroIntroDelay,
            onComplete : () => {
                contentElement.style.pointerEvents = 'auto';
                this.isStageOneInteractive         = true;
            }
        });

        // Animate background first
        if (backgroundElement) {
            timeline.from(
                backgroundElement,
                this.getHeroBackgroundAnimationConfig()
            );
        }

        // Start text animations with a small delay after background
        const textDelay = backgroundElement ? 0.15 : 0;

        [headerElement, titleElement].forEach((textElement) => {
            if (!textElement) {
                return;
            }

            if (this.isIOS) {
                timeline.from(
                    textElement,
                    HERO_TITLE_ANIMATION_IOS,
                    textDelay
                );
                return;
            }

            const splitText = new SplitText(textElement, { type : 'lines' });

            timeline.from(
                splitText.lines,
                HERO_TITLE_ANIMATION_DESKTOP,
                textDelay
            );
        });

        if (buttonElement) {
            const buttonDelay = backgroundElement ? 0.05 : 0;

            if (this.isIOS) {
                timeline.from(
                    buttonElement,
                    HERO_BUTTON_ANIMATION_IOS,
                    `-=${0.1 - buttonDelay}`
                );
            } else {
                timeline.from(
                    buttonElement,
                    HERO_BUTTON_ANIMATION_DESKTOP,
                    `-=${0.1 - buttonDelay}`
                );
            }
        }
    }

    /**
     * Hide hero content for stage one when transitioning to stage two.
     */
    hideHeroStageOne() {
        if (!this.overlayElement) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-1');

        if (!contentElement) {
            return;
        }

        const headerElement = document.querySelector('.experience-header');
        if (headerElement) {
            headerElement.classList.add('experience-header--is-hidden');
        }

        gsap.to(contentElement, {
            opacity    : 0,
            y          : -20,
            duration   : 0.4,
            ease       : 'power2.in',
            onComplete : () => {
                contentElement.style.pointerEvents = 'none';
                this.isStageOneInteractive         = false;
            }
        });
    }

    /**
     * Show hero content for stage three when video reaches stageThreeTargetPercentage.
     */
    showHeroStageThree() {
        if (!this.overlayElement || this.hasStageThreeShown) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-3');

        if (!contentElement) {
            return;
        }
        const titleElement      = contentElement.querySelector('.experience-hero__title');
        const buttonElement     = contentElement.querySelector('.experience-hero__button');
        const backgroundElement = contentElement.querySelector('.experience-hero__background');

        this.hasStageThreeShown = true;

        gsap.set(contentElement, {
            opacity : 1,
            y       : 0
        });

        const timeline = gsap.timeline({
            onComplete : () => {
                contentElement.style.pointerEvents = 'auto';
                this.isStageThreeInteractive       = true;
            }
        });

        // Animate background first
        if (backgroundElement) {
            timeline.from(
                backgroundElement,
                this.getHeroBackgroundAnimationConfig()
            );
        }

        if (titleElement) {
            if (this.isIOS) {
                timeline.from(
                    titleElement,
                    HERO_TITLE_ANIMATION_IOS,
                    backgroundElement ? 0.15 : 0
                );
            } else {
                const splitTitle = new SplitText(titleElement, { type : 'lines' });

                timeline.from(
                    splitTitle.lines,
                    HERO_TITLE_ANIMATION_DESKTOP,
                    backgroundElement ? 0.15 : 0
                );
            }
        }

        if (buttonElement) {
            const buttonDelay = backgroundElement ? 0.05 : 0;

            if (this.isIOS) {
                timeline.from(
                    buttonElement,
                    HERO_BUTTON_ANIMATION_IOS,
                    `-=${0.1 - buttonDelay}`
                );
            } else {
                timeline.from(
                    buttonElement,
                    HERO_BUTTON_ANIMATION_DESKTOP,
                    `-=${0.1 - buttonDelay}`
                );
            }
        }
    }

    /**
     * Hide hero content for stage three when transitioning to scroll section.
     */
    hideHeroStageThree() {
        if (!this.overlayElement) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-3');

        if (!contentElement) {
            return;
        }

        gsap.to(contentElement, {
            opacity    : 0,
            y          : -20,
            duration   : 0.4,
            ease       : 'power2.in',
            onComplete : () => {
                contentElement.style.pointerEvents = 'none';
                this.isStageThreeInteractive       = false;
            }
        });
    }

    /**
     * Show hero content for stage one with simple fade (used on reverse).
     */
    showHeroStageOneSimple() {
        if (!this.overlayElement) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-1')
            || this.overlayElement.querySelector('.experience-hero__content');

        if (!contentElement) {
            return;
        }
        gsap.to(contentElement, {
            opacity    : 1,
            y          : 0,
            duration   : 0.4,
            ease       : 'power2.out',
            onComplete : () => {
                contentElement.style.pointerEvents = 'auto';
                this.isStageOneInteractive         = true;
            }
        });
    }

    /**
     * Show hero content for stage two when video reaches stageTwoTargetPercentage.
     */
    showHeroStageTwo() {
        if (!this.overlayElement || this.hasStageTwoShown) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-2');

        if (!contentElement) {
            return;
        }
        const titleElement      = contentElement.querySelector('.experience-hero__title');
        const buttonElement     = contentElement.querySelector('.experience-hero__button');
        const backgroundElement = contentElement.querySelector('.experience-hero__background');

        this.hasStageTwoShown = true;

        gsap.set(contentElement, {
            opacity : 1,
            y       : 0
        });

        const timeline = gsap.timeline({
            onComplete : () => {
                contentElement.style.pointerEvents = 'auto';
                this.isStageTwoInteractive         = true;
            }
        });

        // Animate background first
        if (backgroundElement) {
            timeline.from(
                backgroundElement,
                this.getHeroBackgroundAnimationConfig()
            );
        }

        if (titleElement) {
            if (this.isIOS) {
                timeline.from(
                    titleElement,
                    {
                        opacity  : 0,
                        y        : 24,
                        duration : 0.55,
                        ease     : 'power2.out'
                    },
                    backgroundElement ? 0.15 : 0
                );
            } else {
                const splitTitle = new SplitText(titleElement, { type : 'lines' });

                timeline.from(
                    splitTitle.lines,
                    {
                        opacity  : 0,
                        y        : 10,
                        filter   : 'blur(3px)',
                        stagger  : 0.1,
                        duration : 0.5,
                        ease     : 'power2.out'
                    },
                    backgroundElement ? 0.15 : 0
                );
            }
        }

        if (buttonElement) {
            const buttonDelay = backgroundElement ? 0.05 : 0;

            if (this.isIOS) {
                timeline.from(
                    buttonElement,
                    {
                        opacity  : 0,
                        y        : 12,
                        scale    : 0.95,
                        duration : 0.35,
                        ease     : 'power2.out'
                    },
                    `-=${0.1 - buttonDelay}`
                );
            } else {
                timeline.from(
                    buttonElement,
                    {
                        opacity  : 0,
                        y        : 12,
                        scale    : 0.9,
                        skewY    : 3,
                        filter   : 'blur(2px)',
                        duration : 0.4,
                        ease     : 'back.out(1.7)'
                    },
                    `-=${0.1 - buttonDelay}`
                );
            }
        }
    }

    showHeroStageTwoSimple() {
        if (!this.overlayElement) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-2');

        if (!contentElement) {
            return;
        }

        const backgroundElement = contentElement.querySelector('.experience-hero__background');

        // Animate background first
        if (backgroundElement) {
            if (this.isSafari) {
                gsap.fromTo(
                    backgroundElement,
                    {
                        opacity : 0
                    },
                    {
                        opacity  : 1,
                        duration : 0.6,
                        ease     : 'power2.out'
                    }
                );
            } else {
                gsap.fromTo(
                    backgroundElement,
                    {
                        opacity : 0,
                        scale   : 1.1
                    },
                    {
                        opacity  : 1,
                        scale    : 1,
                        duration : 0.6,
                        ease     : 'power2.out'
                    }
                );
            }
        }

        gsap.to(contentElement, {
            opacity    : 1,
            y          : 0,
            duration   : 0.4,
            ease       : 'power2.out',
            onComplete : () => {
                contentElement.style.pointerEvents = 'auto';
                this.isStageTwoInteractive         = true;
            }
        });
    }

    /**
     * Hide hero content for stage two when transitioning to scroll section.
     */
    hideHeroStageTwo() {
        if (!this.overlayElement) {
            return;
        }

        const contentElement = this.overlayElement.querySelector('.experience-hero__content--stage-2');

        if (!contentElement) {
            return;
        }
        gsap.to(contentElement, {
            opacity    : 0,
            y          : -20,
            duration   : 0.4,
            ease       : 'power2.in',
            onComplete : () => {
                contentElement.style.pointerEvents = 'none';
                this.isStageTwoInteractive         = false;
            }
        });
    }

    /**
     * Start snow animation when hero is visible.
     */
    startSnow() {
        if (this.snowAnimation) {
            return;
        }

        const canvasSelector = '.experience-hero__snow';
        const canvasElement  = document.querySelector(canvasSelector);

        if (!canvasElement) {
            return;
        }

        this.snowAnimation = new SnowAnimation(canvasSelector);
        this.snowAnimation.animate();
    }

    /**
     * Stop snow animation when leaving hero experience.
     */
    stopSnow() {
        if (!this.snowAnimation) {
            return;
        }

        this.snowAnimation.stop();

        this.snowAnimation = null;

        const canvasSelector = '.experience-hero__snow';
        const canvasElement  = document.querySelector(canvasSelector);

        if (canvasElement) {
            gsap.to(canvasElement, {
                opacity  : 0,
                duration : 0.6,
                ease     : 'power2.out'
            });
        }
    }

    /**
     * Move ScrollyVideo to stage-one percentage and freeze.
     */
    videoStageOne() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function' || !this.isReady) {
            this.shouldRunStageOne = true;
            return;
        }

        this.shouldRunStageOne    = false;
        this.hasStageOneCompleted = true;
        this.heroIntroPending     = true;

        // Test if video can play (detect low power mode on iOS)
        this.testVideoPlayback();

        this.scrollyInstance.setVideoPercentage(this.stageOneTargetPercentage, {
            transitionSpeed : 1
        });
    }

    /**
     * Test if video playback is possible (detect iOS low power mode).
     * If playback fails, automatically skip to slides mode.
     */
    testVideoPlayback() {
        if (!this.scrollyInstance || !this.scrollyInstance.video) {
            return;
        }

        const { video } = this.scrollyInstance;

        // Try to play video briefly to test if it's allowed
        const playPromise = video.play();

        if (playPromise && typeof playPromise.then === 'function') {
            playPromise
                .then(() => {
                    // Playback started successfully, pause immediately
                    video.pause();

                    if (VideoExperience.isDebugEnabled()) {
                        // eslint-disable-next-line no-console
                        console.log('[VideoExperience] Video playback test: SUCCESS');
                    }
                })
                .catch((error) => {
                    // Playback failed (likely iOS low power mode or autoplay restrictions)
                    // eslint-disable-next-line no-console
                    console.warn('[VideoExperience] Video playback test FAILED, skipping to slides mode', { error });

                    // Dispatch event to skip to slides
                    const event = new CustomEvent('video-playback-failed', {
                        detail : { reason : 'autoplay-blocked' }
                    });
                    window.dispatchEvent(event);
                });
        }
    }

    /**
     * Move ScrollyVideo to stage-two percentage.
     */
    videoStageTwo() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function') {
            console.log('ScrollyVideo not initialized');
            return;
        }

        if (!this.hasStageOneCompleted) {
            console.log('[VideoExperience] Stage one not completed', {
                isReady              : this.isReady,
                hasStageOneCompleted : this.hasStageOneCompleted
            });
            return;
        }

        if (this.timeline && this.timeline.isActive && this.timeline.isActive()) {
            console.log('Timeline is active');
            return;
        }

        this.hasStageTwoShown = false;

        if (this.scrollyInstance.video && this.scrollyInstance.video.duration) {
            const { video }    = this.scrollyInstance;
            const percentage = video.currentTime / video.duration;

            // eslint-disable-next-line no-console
            console.log('[VideoExperience] videoStageTwo start', {
                percentage,
                hasStageOneCompleted  : this.hasStageOneCompleted,
                isStageOneInteractive : this.isStageOneInteractive
            });
        } else {
            // eslint-disable-next-line no-console
            console.log('[VideoExperience] videoStageTwo start (no duration)', {
                hasStageOneCompleted  : this.hasStageOneCompleted,
                isStageOneInteractive : this.isStageOneInteractive
            });
        }

        this.hideHeroStageOne();

        const transitionSpeed = this.forwardStageTransitionSpeed;

        this.scrollyInstance.setVideoPercentage(this.stageTwoTargetPercentage, { transitionSpeed });

        // eslint-disable-next-line no-console
        console.log('[VideoExperience] videoStageTwo setVideoPercentage', {
            target                : this.stageTwoTargetPercentage,
            transitionSpeed,
            isStageOneInteractive : this.isStageOneInteractive
        });
    }

    /**
     * Reverse from scroll-mode back to stage-two (video-phase-2).
     * Restores second hero state and snow, rewinds video 1.0 → stageTwoTargetPercentage.
     * Currently not used for main flow but kept for potential future scenarios.
     */
    reverseFromScrollModeToPhaseTwo() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function') {
            // eslint-disable-next-line no-console
            console.log('ScrollyVideo not initialized');
            return;
        }

        this.hasStageTwoShown                   = true;
        this.isStageTwoInteractive              = false;
        this.isReversingFromScrollMode          = true;
        this.hasStageTwoRevealPendingFromScroll = true;

        if (this.overlayElement) {
            const stageOneContent = this.overlayElement.querySelector('.experience-hero__content--stage-1')
                || this.overlayElement.querySelector('.experience-hero__content');
            const stageTwoContent = this.overlayElement.querySelector('.experience-hero__content--stage-2');
            const bgStageOne      = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-1');
            const bgStageTwo      = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-2');

            if (stageOneContent) {
                gsap.set(stageOneContent, {
                    opacity : 0,
                    y       : -20
                });
                stageOneContent.style.pointerEvents = 'none';
            }

            if (bgStageOne) {
                gsap.set(bgStageOne, {
                    opacity : 0
                });
            }

            if (stageTwoContent) {
                gsap.set(stageTwoContent, {
                    opacity : 0,
                    y       : 0
                });
                stageTwoContent.style.pointerEvents = 'none';
            }

            if (bgStageTwo) {
                gsap.set(bgStageTwo, {
                    opacity : 0
                });
            }
        }

        this.startSnow();

        this.scrollyInstance.setVideoPercentage(this.stageTwoTargetPercentage, {
            transitionSpeed : this.reverseScrollModeTransitionSpeed
        });
    }

    /**
     * Reverse from stage three back to stage two (video-phase-3 → video-phase-2).
     * Hides third hero, rewinds video stageThreeTargetPercentage → stageTwoTargetPercentage
     * and reveals second hero when target percentage is reached.
     */
    reverseStageThreeToTwo() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function') {
            // eslint-disable-next-line no-console
            console.log('ScrollyVideo not initialized');
            return;
        }

        this.hasStageTwoShown         = true;
        this.hasStageThreeShown       = false;
        this.hasStageThreeShown       = false;
        this.isStageThreeInteractive  = false;
        this.isReversingToStageTwo    = true;
        this.hasStageTwoRevealPending = true;

        if (this.shellElement) {
            this.shellElement.classList.remove('experience-hero__video-shell--stage-3-focus');
        }

        if (this.overlayElement) {
            const stageTwoContent   = this.overlayElement.querySelector('.experience-hero__content--stage-2');
            const stageThreeContent = this.overlayElement.querySelector('.experience-hero__content--stage-3');
            const bgStageTwo        = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-2');
            const bgStageThree      = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-3');

            if (stageThreeContent) {
                gsap.set(stageThreeContent, {
                    opacity : 0,
                    y       : -20
                });
                stageThreeContent.style.pointerEvents = 'none';
            }

            if (bgStageThree) {
                gsap.set(bgStageThree, {
                    opacity : 0
                });
            }

            if (stageTwoContent) {
                gsap.set(stageTwoContent, {
                    opacity : 0,
                    y       : 0
                });
                stageTwoContent.style.pointerEvents = 'none';
            }

            if (bgStageTwo) {
                gsap.set(bgStageTwo, {
                    opacity : 0
                });
            }
        }

        this.startSnow();

        this.scrollyInstance.setVideoPercentage(this.stageTwoTargetPercentage, {
            transitionSpeed : this.reverseStageTransitionSpeed
        });
    }

    /**
     * Reverse from scroll-mode back to stage three (video-phase-3).
     * Restores third hero state and snow, rewinds video 1.0 → stageThreeTargetPercentage.
     */
    reverseFromScrollModeToPhaseThree() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function') {
            // eslint-disable-next-line no-console
            console.log('ScrollyVideo not initialized');
            return;
        }

        if (this.overlayElement) {
            const stageOneContent   = this.overlayElement.querySelector('.experience-hero__content--stage-1')
                || this.overlayElement.querySelector('.experience-hero__content');
            const stageTwoContent   = this.overlayElement.querySelector('.experience-hero__content--stage-2');
            const stageThreeContent = this.overlayElement.querySelector('.experience-hero__content--stage-3');
            const bgStageOne        = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-1');
            const bgStageTwo        = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-2');
            const bgStageThree      = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-3');

            if (stageOneContent) {
                gsap.set(stageOneContent, {
                    opacity : 0,
                    y       : -20
                });
                stageOneContent.style.pointerEvents = 'none';
            }

            if (bgStageOne) {
                gsap.set(bgStageOne, {
                    opacity : 0
                });
            }

            if (stageTwoContent) {
                gsap.set(stageTwoContent, {
                    opacity : 0,
                    y       : -20
                });
                stageTwoContent.style.pointerEvents = 'none';
            }

            if (bgStageTwo) {
                gsap.set(bgStageTwo, {
                    opacity : 0
                });
            }

            if (stageThreeContent) {
                gsap.set(stageThreeContent, {
                    opacity : 0,
                    y       : 0
                });
                stageThreeContent.style.pointerEvents = 'none';
            }

            if (bgStageThree) {
                gsap.set(bgStageThree, {
                    opacity : 0
                });
            }
        }

        this.startSnow();

        this.hasStageThreeShown      = false;
        this.isStageThreeInteractive = false;

        this.scrollyInstance.setVideoPercentage(this.stageThreeTargetPercentage, {
            transitionSpeed : this.reverseScrollModeTransitionSpeed
        });

        this.showHeroStageThree();
    }

    /**
     * Move ScrollyVideo to stage-three percentage.
     */
    videoStageThree() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function') {
            console.log('ScrollyVideo not initialized');
            return;
        }

        if (!this.hasStageTwoShown) {
            console.log('[VideoExperience] Stage two not shown');
            return;
        }

        if (this.timeline && this.timeline.isActive && this.timeline.isActive()) {
            console.log('Timeline is active');
            return;
        }

        if (this.shellElement) {
            this.shellElement.classList.add('experience-hero__video-shell--stage-3-focus');
        }
        console.log('Stage three focus');

        this.hideHeroStageTwo();

        this.hasStageThreeShown      = false;
        this.isStageThreeInteractive = false;

        const transitionSpeed = this.forwardStageTransitionSpeed;

        this.scrollyInstance.setVideoPercentage(this.stageThreeTargetPercentage, {
            transitionSpeed
        });
    }

    /**
     * Move ScrollyVideo from stage three to final percentage before entering scroll-mode.
     */
    videoToScrollEnd() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function') {
            console.log('ScrollyVideo not initialized');
            return;
        }

        if (!this.hasStageTwoShown) {
            console.log('[VideoExperience] Stage two not shown');
        }

        if (this.timeline && this.timeline.isActive && this.timeline.isActive()) {
            console.log('Timeline is active');
            return;
        }

        this.hideHeroStageTwo();
        this.stopSnow();

        const { video } = this.scrollyInstance;

        if (!video) {
            return;
        }

        if (this.scrollyInstance.transitioningRaf) {
            window.cancelAnimationFrame(this.scrollyInstance.transitioningRaf);
            this.scrollyInstance.transitioningRaf = null;
        }

        video.playbackRate = 1;

        try {
            const playPromise = video.play();

            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => null);
            }
        } catch (error) {
            if (VideoExperience.isDebugEnabled()) {
                // eslint-disable-next-line no-console
                console.log('[VideoExperience] video.play() failed', { error });
            }
        }
    }

    /**
     * Reverse from stage-two back to stage-one (video-phase-2 → video-phase-1).
     * Hides second hero, rewinds video stageTwoTargetPercentage → stageOneTargetPercentage
     * and reveals first hero when target percentage is reached.
     */
    reverseToStageOne() {
        if (!this.scrollyInstance || typeof this.scrollyInstance.setVideoPercentage !== 'function') {
            // eslint-disable-next-line no-console
            console.log('ScrollyVideo not initialized');
            return;
        }

        if (this.isMobile && this.shellElement) {
            this.shellElement.classList.remove('experience-hero__video-shell--scroll-zoom');
        }

        this.hasStageOneCompleted               = true;
        this.isStageOneInteractive              = false;
        this.isStageTwoInteractive              = false;
        this.isReversingToStageOne              = true;
        this.hasStageOneRevealPending           = true;
        this.isReversingFromScrollMode          = false;
        this.hasStageTwoRevealPendingFromScroll = false;

        if (this.overlayElement) {
            const stageOneContent = this.overlayElement.querySelector('.experience-hero__content--stage-1')
                || this.overlayElement.querySelector('.experience-hero__content');
            const bgStageOne      = this.overlayElement.querySelector('.experience-hero__overlay-layer--stage-1');

            if (stageOneContent) {
                gsap.set(stageOneContent, {
                    opacity : 0,
                    y       : 0
                });
                stageOneContent.style.pointerEvents = 'none';
            }

            if (bgStageOne) {
                gsap.set(bgStageOne, {
                    opacity : 0
                });
            }
        }

        // Hide second hero with existing animation
        this.hideHeroStageTwo();

        // Ensure snow is running during reverse
        this.startSnow();

        // Start reverse playback towards stage one percentage
        this.scrollyInstance.setVideoPercentage(this.stageOneTargetPercentage, {
            transitionSpeed : this.reverseStageTransitionSpeed
        });
    }

    /**
     * Clean up all resources and animations.
     */
    destroy() {
        // Kill all GSAP animations using manager
        gsapManager.killTweensOf(this.shellElement);
        gsapManager.killTweensOf(this.overlayElement);

        // Clean up specific animations
        if (this.timeline) {
            this.timeline.kill();
            this.timeline = null;
        }

        // Clean up snow animation
        if (this.snowAnimation) {
            this.snowAnimation.destroy();
            this.snowAnimation = null;
        }

        // Clean up ScrollyVideo instance
        if (this.scrollyInstance) {
            if (this.scrollyInstance.video) {
                this.scrollyInstance.video.removeEventListener('timeupdate', this.handleVideoTimeUpdate);
                this.scrollyInstance.video.pause();
                this.scrollyInstance.video.src = '';
            }

            if (this.scrollyInstance.transitioningRaf) {
                window.cancelAnimationFrame(this.scrollyInstance.transitioningRaf);
                this.scrollyInstance.transitioningRaf = null;
            }

            this.scrollyInstance.destroy();
            this.scrollyInstance = null;
        }

        // Reset element references
        this.shellElement = null;
        this.overlayElement = null;

        // Reset state flags
        this.isReady = false;
        this.shouldRunStageOne = false;
        this.hasStageOneCompleted = false;
        this.heroIntroDelay = 0;
        this.heroIntroShown = false;
        this.heroIntroPending = false;
        this.hasStageTwoShown = false;
        this.hasStageThreeShown = false;
        this.isStageOneInteractive = false;
        this.isStageTwoInteractive = false;
        this.isStageThreeInteractive = false;
        this.isReversingToStageOne = false;
        this.hasStageOneRevealPending = false;
        this.isReversingToStageTwo = false;
        this.hasStageTwoRevealPending = false;
        this.isReversingFromScrollMode = false;
        this.hasStageTwoRevealPendingFromScroll = false;
    }
}

export default VideoExperience;
